import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../../Spinner';
import {Brain, CaretDown, FileText, Lightning, Lock, MagnifyingGlass, Warning} from '../../../icons';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {normalizeText} from '../../../../utils/textNormalize';
import {
    FACT_TYPE_MODIFIER, FACT_TYPE_ORDER, VISIBILITY, factTypeLabelKey, isSensitive, originLabelKey,
} from '../../memory/factConstants';
import {FactEditModal} from '../../memory/FactEditModal';
import {BOOSTER_STEP_FEED, boosterFlowPath} from '../boosterSteps';
import {IaContextSheet} from './IaContextSheet';
import {isAutoEntered, useClientFacts} from '../facts/ClientFactsStore';
import {useSourcesDrawer} from '../feed/sourcesDrawerContext';
import {useSourceViewer} from '../sourceViewerContext';

/**
 * El archivo de Hechos: lo que el equipo aprobó, agrupado por tipo, buscable y curable.
 *
 * Tres cosas que el mockup hacía mal y acá no:
 *
 * 1. **Buscar «martin» encuentra «Martín»** y buscar «cliente» NO devuelve todo. El mockup
 *    matcheaba `textContent` del nodo entero —o sea también la metadata «Dicho por el cliente»— y
 *    comparaba sin normalizar acentos. Acá se busca sobre el `statement` (y el texto de la nota),
 *    con acentos plegados.
 * 2. **Están las ONCE categorías**, no seis. Con seis chips había hechos que ningún filtro podía
 *    alcanzar. Y un chip cuyo contador da 0 no desaparece: se muestra deshabilitado con `(0)`
 *    —aparecer/desaparecer se lee como un estado del botón— salvo que esté prendido, porque
 *    entonces hay que poder apagarlo.
 * 3. **Ningún botón muerto.** En el mockup los ~38 «Editar»/«Borrar» no tenían handler. Acá cada
 *    acción hace lo que dice, y lo que no se iba a implementar no se dibuja.
 *
 * Las notas manuales se pliegan como un grupo más al final ("Notas tuyas"): son memoria del
 * cliente igual que un hecho, y tenerlas en una pestaña aparte obligaba a acordarse de mirarlas.
 *
 * **Lo que entró solo vive ACÁ, no en una sección aparte.** Hasta el 2026-09-08 los hechos que el
 * gate dejó pasar sin una persona se auditaban en un bloque propio al pie de Revisa
 * (`AutoEnteredSection`), o sea *antes* de "Qué sabemos de X" y fuera de él. Eran los mismos hechos
 * que el archivo ya listaba —`library` es aprobado + vigente, y un hecho que entró solo cumple las
 * dos—, así que la pantalla los mostraba dos veces y con dos sentidos contradictorios: "estos no
 * los tenés que revisar" arriba y "estos son parte de tu memoria" abajo. Ahora la fila lleva su
 * marca «Entró solo» donde el hecho ya estaba.
 *
 * **Un verbo por operación** (card EYxmjBIR, reunión del 10/09). La fila de un hecho que entró
 * solo llegó a mostrar cinco controles —Revisar · Quitar · Cliente · Editar · Borrar— y Pablo,
 * mirándola: «tengo el botón quitar y el botón borrar, ¿cuál es la diferencia?». No la había para
 * él: «Quitar» rechazaba (`reject`, con Deshacer y fila en la Bitácora) y «Borrar» hacía un
 * soft-delete (`DELETE`, con modal de confirmación, sin vuelta y sin rastro), y las dos sacan el
 * hecho de la memoria y del contexto del agente. La que sobrevive es la reversible: **está en
 * TODAS las filas** y «Borrar» no se dibuja más (el endpoint sigue existiendo para la Memoria
 * clásica). Y «Revisar» pasó a decir **«Mandar a revisión»**: Pablo lo apretó creyendo que iba a
 * ver de dónde salía el hecho, y lo que hizo fue mandarlo a la cola. Ese verbo sigue siendo sólo
 * de lo que entró solo —es la salida propia de la marca, «esto no lo miró nadie»— y va pegado a
 * ella, en la línea de procedencia; los verbos que valen para cualquier hecho (Editar, Rechazar)
 * van en la columna de acciones.
 *
 * **El verbo que saca el hecho se llama «Rechazar», igual que en el mazo** (decisión de fede,
 * 2026-09-14). Se llamó «Quitar» un día, y en ese día la misma operación tenía TRES nombres en la
 * misma pantalla: «Rechazar» en el mazo, su estampa y su atajo; «Quitar» acá; y «descartado» en el
 * toast del mazo. Gana «Rechazar» por tres razones y ninguna es de gusto: es el par natural de
 * «Aprobar», que está grabado en la estampa del swipe; lo usaban ya seis superficies contra una; y
 * es lo que la Bitácora escribe para las DOS (`MEMORY_FACT_REJECTED` → «Hecho rechazado»), así que
 * «Quitar» quedaba desmentido por su propio rastro. El toast del mazo se alineó en el mismo
 * movimiento.
 *
 * Y una cuarta: **"no pude leer" NO es "está vacía"**. El store expone `loadFailed`; sin mirarlo,
 * un `GET client-fact/` caído dejaba `library=[]` con `loading=false` y el archivo anunciaba
 * "Todavía no hay nada en esta memoria · Cuando cargues una reunión…" — el mismo store, en la
 * misma página, contestando lo contrario que el mazo de Revisa, que sí distingue los dos casos.
 * Se reusan su copy y su acción (`ia_booster.deck.load_error` / `.retry`): es la misma falla, y dos
 * frases distintas para la misma falla se leen como dos problemas distintos.
 */

// La búsqueda espera a que el usuario deje de tipear. Sin esto, cada tecla re-filtra y re-agrupa
// la biblioteca entera (y con 300 hechos se siente).
const SEARCH_DEBOUNCE_MS = 250;

const NOTES_GROUP = 'NOTES';

// Cuántos hechos muestra un grupo antes de «Ver los N». Con 300 hechos aprobados, el archivo entero
// desplegado es un muro de doce pantallas que nadie lee de corrido: cada grupo abre con una muestra
// y se despliega a pedido (mockup: `showMore`). Con una búsqueda activa se muestra TODO lo que
// matchea — el usuario ya dijo qué busca, y esconderle resultados detrás de un botón sería
// contestarle a medias.
export const GROUP_PREVIEW = 3;

/**
 * A partir de cuántos hechos el archivo abre CERRADO, un renglón por categoría con su conteo.
 *
 * Con la muestra de 3 por grupo, una memoria grande sigue siendo un muro: medido en pantalla con
 * Dorfman (62 hechos + 11 notas en 9 grupos), el archivo mide **3.460 px — 2,8 pantallas** sin
 * desplegar un solo grupo, y Rouge (Bakián) tiene 300 y pico. Cerrado, esa misma memoria entra en
 * una pantalla y el usuario elige por dónde entrar: es la diferencia entre recorrer un índice y
 * scrollear una lista.
 *
 * El umbral es 40 y no un número redondo mayor porque lo que importa no es el total sino cuántos
 * grupos abiertos hay debajo: con ≤ 40 hechos ningún grupo pasa la muestra por mucho y el archivo
 * abierto se lee de una. Es un DEFAULT, no un modo: cualquier grupo se abre con un click y
 * «Abrir todo» devuelve la vista de siempre.
 */
export const ARCHIVE_COLLAPSE_MIN = 40;

// Prefijo del id del aviso de cada hecho: el id REAL lleva el id del hecho pegado. Ver `notify`.
const TOAST_ID = 'iabm-archive-auto';

/**
 * Cuánto queda a la vista el aviso de «Rechazar», que es el que trae el Deshacer.
 *
 * Desde que «Borrar» no existe, «Rechazar» es la ÚNICA salida de un hecho del archivo y no pide
 * confirmación: su red es este aviso. Los 5 s del `<ToastContainer>` (`App.js`) están pensados para
 * leer una frase, no para arrepentirse de algo que se hace decenas de veces por sesión, y la
 * cadena de vuelta ya tarda del orden de 1,5 s. El resto de los avisos del archivo no lo necesita.
 */
export const UNDO_TOAST_MS = 8000;

/**
 * A dónde lleva «Ver planes» cuando los Hechos no están en el plan: la Suscripción, abierta en la
 * sección para cambiar de plan (el mismo destino que «Mejorar plan» del cupo del portal).
 */
export const PLANS_PATH = '/agency/subscription#plan-change';

/**
 * `factsLocked`: la Memoria del Cliente está en OFF (lo decide `MemoryDuo` con
 * `useFactsLayerLocked`). Ahí no hay acto Alimenta, así que el vacío de siempre —«Cargar material»,
 * un link a `#alimenta`— llevaba a un ancla que no existe y el clic no hacía nada. La bandeja dice
 * que la memoria de hechos no está en el plan y lleva a Suscripción.
 */
export function MemoryArchiveView({clientId, onFilterState, factsLocked = false}) {
    const {t} = useTranslation();
    /**
     * Dónde cae el foco cuando una fila se desmonta bajo el cursor de teclado.
     *
     * «Quitar» saca la fila de forma optimista: el `<button>` que tenía el foco deja de existir y el
     * navegador lo manda al `<body>`, o sea que quien navega con teclado pierde el lugar en una
     * lista que puede tener 300 filas (C-verbos-05). Con el modal de confirmación esto no pasaba
     * —tomaba el foco y lo devolvía—, así que es una regresión que trajo sacarlo. El ancla es el
     * contenedor de grupos, que sobrevive a la fila: `tabIndex={-1}` lo hace enfocable por código
     * sin meterlo en el orden de tabulación.
     */
    const groupsRef = useRef(null);
    const {library, documentsById, loading, loadFailed, isBusy, actions} = useClientFacts();
    const drawer = useSourcesDrawer();
    // El visor del crudo, posicionado en la cita: la trazabilidad de CADA hecho, en su fila. Hasta
    // el 2026-09-12 el archivo sólo tenía el link general a «Documentos y notas» de la cabecera, y
    // verificar de dónde salía un hecho era memorizar el nombre del documento, abrir la biblioteca,
    // encontrarlo a mano y hacer Ctrl+F con una frase que ya no se recordaba (Pablo, 10/09).
    const viewer = useSourceViewer();

    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const [activeTypes, setActiveTypes] = useState(() => new Set());
    // Sólo lo que entró sin una persona. Es un filtro de PROCEDENCIA y se cruza con los de tipo.
    const [autoOnly, setAutoOnly] = useState(false);
    // Los grupos cuyo estado difiere del default. Se guarda la DIFERENCIA y no el estado absoluto
    // porque el default llega después del primer render —la biblioteca la trae el store— y un Set
    // calculado con cero hechos dejaría el archivo abierto justo cuando es grande. Que esa base no
    // se mueva DESPUÉS es lo que sostiene `frozenBase`.
    const [flippedGroups, setFlippedGroups] = useState(() => new Set());
    // `null` = nadie tocó «Abrir todo» / «Cerrar todo»: manda el tamaño del archivo.
    const [allOpen, setAllOpen] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const [sheetOpen, setSheetOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [editTarget, setEditTarget] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    // Qué hechos tienen un Deshacer EN VUELO, por id. Ver `handleRemove`.
    const undoingRef = useRef({});

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchDraft), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchDraft]);

    // Notas manuales. Fail-soft: si el fetch falla, el archivo muestra los hechos igual (una caja
    // de error acá dejaría rota la pantalla entera por una capa secundaria).
    // Con los Hechos fuera del plan la bandeja no dibuja notas: no se piden.
    useEffect(() => {
        if (factsLocked) return undefined;
        let alive = true;
        ClientMemoryApi.listMemories(clientId).then((data) => {
            if (!alive) return;
            const rows = (data && (data.memories || data.data)) || [];
            setNotes(Array.isArray(rows) ? rows : []);
        }).catch(() => { if (alive) setNotes([]); });
        return () => { alive = false; };
    }, [clientId, factsLocked]);

    const needle = normalizeText(search).trim();
    const matches = useMemo(
        () => (text) => !needle || normalizeText(text).includes(needle),
        [needle],
    );

    // Los contadores de los chips salen de lo que la BÚSQUEDA dejó pasar, no de la biblioteca
    // entera: un chip que dice "(9)" y al prenderlo muestra 0 filas porque el texto no matchea es
    // un contador que miente sobre lo que va a hacer el click.
    const searched = useMemo(
        () => library.filter(fact => matches(fact.statement)),
        [library, matches],
    );
    // Cada contador se mide sobre lo que quedaría si SÓLO faltara prender ese chip — o sea cruzado
    // con los OTROS filtros activos, no sólo con la búsqueda. Con «Entró solo» prendido, un chip
    // que decía «Objetivo (9)» abría 2 filas: el mismo defecto que este archivo ya tenía escrito en
    // el comentario de arriba, en la otra mitad del cruce (review 2026-09-09, R-03).
    const typeCounts = useMemo(() => {
        const counts = {};
        searched.forEach((fact) => {
            if (autoOnly && !isAutoEntered(fact)) return;
            const type = fact.fact_type || 'OTHER';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [searched, autoOnly]);

    const autoCount = useMemo(() => searched.filter(
        fact => isAutoEntered(fact) && (!activeTypes.size || activeTypes.has(fact.fact_type)),
    ).length, [searched, activeTypes]);

    const filtered = useMemo(() => {
        let rows = searched;
        if (activeTypes.size) rows = rows.filter(fact => activeTypes.has(fact.fact_type));
        if (autoOnly) rows = rows.filter(isAutoEntered);
        return rows;
    }, [searched, activeTypes, autoOnly]);

    const groups = useMemo(() => (
        FACT_TYPE_ORDER
            .map(type => ({type, items: filtered.filter(fact => fact.fact_type === type)}))
            .filter(group => group.items.length > 0)
    ), [filtered]);

    // El duo dibuja el número de Hechos 300px más arriba y NO conoce este filtro: sin esto la
    // tarjeta seguía diciendo "45 aprobados" con la lista vacía debajo, justo mientras su tarjeta
    // hermana (el sismógrafo) sí respondía al filtro. Dos tarjetas gemelas con reglas distintas es
    // lo que rompe la lectura, más que el número en sí.
    const filterActive = Boolean(needle) || activeTypes.size > 0 || autoOnly;
    useEffect(() => {
        if (!onFilterState) return;
        onFilterState({active: filterActive, shown: filtered.length});
    }, [onFilterState, filterActive, filtered.length]);

    // Y al DESMONTARSE lo suelta. Cambiar a la bandeja de Historial con un filtro puesto dejaba a
    // la tarjeta «Hechos» del duo con el conteo filtrado de un archivo que ya no está en pantalla
    // (review 2026-09-09, R-09e). Va en su propio efecto de montaje —con el callback por ref— para
    // no soltar y volver a poner el número en cada cambio de filtro.
    const onFilterStateRef = useRef(onFilterState);
    onFilterStateRef.current = onFilterState;
    useEffect(() => () => {
        if (onFilterStateRef.current) onFilterStateRef.current({active: false, shown: null});
    }, []);

    // Con un filtro de tipo activo las notas no aplican (no tienen tipo de hecho): el grupo se
    // oculta como cualquier otro sin resultados. Con «Entró solo» tampoco: una nota la escribió
    // una persona, así que por definición no entró sola.
    const visibleNotes = activeTypes.size || autoOnly
        ? []
        : notes.filter(note => matches(note.text));

    const toggleType = (type) => {
        setActiveTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const toggleExpanded = (key) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleGroup = (key) => {
        // La diferencia se mide contra la base que estaba en efecto cuando la diferencia empezó.
        if (frozenBase === null) setFrozenBase(liveBaseOpen);
        setFlippedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    /**
     * El default se CONGELA en cuanto el usuario toca un grupo, y no se mueve más.
     *
     * `flippedGroups` guarda la DIFERENCIA contra este valor, así que si el valor cambia debajo, lo
     * que el usuario abrió se cierra y lo que no, se abre — el archivo se invierte solo. Con la
     * fórmula viva pasaba de verdad (review 2026-09-09, R-02): con 41 hechos el archivo abre
     * cerrado, el usuario abre un grupo, aprieta «Quitar» → quedan 40 → el default pasa a «abierto»
     * → **el grupo que abrió se cierra y los otros ocho se abren**. Y al revés aprobando una carta
     * en el mazo, que come del mismo store y está en la misma página.
     *
     * Congelar EN LA INTERACCIÓN y no «cuando los listados asienten» es lo que lo cubre entero:
     * mientras nadie tocó nada, `flippedGroups` está vacío y que la base se mueva no invierte nada;
     * y no depende de que un fetch conteste (el de las notas puede no contestar nunca).
     *
     * El tamaño es el del archivo ENTERO, no el de lo filtrado: el default habla de esta memoria.
     * Queda un resto conocido: entre 30 y 40 hechos, las notas llegan por un fetch aparte y el
     * archivo puede pintarse abierto y cerrarse unos ms después. Es un parpadeo, no una inversión.
     */
    const archiveSize = library.length + notes.length;
    const liveBaseOpen = archiveSize <= ARCHIVE_COLLAPSE_MIN;
    const [frozenBase, setFrozenBase] = useState(null);
    const baseOpen = allOpen !== null
        ? allOpen
        : (frozenBase !== null ? frozenBase : liveBaseOpen);

    // FILTRAR ES PEDIR VER. Con cualquier filtro puesto —la búsqueda, un chip de categoría, «Entró
    // solo»— los grupos que quedan se abren solos: si no, el usuario acota y se encuentra con un
    // encabezado y un contador que todavía tiene que abrir a mano, o sea que su click no hizo lo
    // que pidió. Antes esto valía sólo para la búsqueda, y con el archivo cerrado por tamaño el
    // chip de categoría quedaba a mitad de camino.
    const isOpen = (key) => (filterActive ? true : (flippedGroups.has(key) ? !baseOpen : baseOpen));

    // «Abrir todo» / «Cerrar todo» fija la base y borra las diferencias: si no, un grupo que el
    // usuario había cerrado a mano seguiría cerrado después de pedir abrir todo — el botón diría
    // una cosa y la pantalla otra.
    const setAllGroups = (open) => {
        setAllOpen(open);
        setFlippedGroups(new Set());
        setFrozenBase(null);
    };

    const clearFilters = () => {
        setSearchDraft('');
        setSearch('');
        setActiveTypes(new Set());
        setAutoOnly(false);
    };

    const handleEditSave = (fact, statement) => {
        setSavingEdit(true);
        ClientMemoryApi.patchFact(fact.id, {agency_client_id: clientId, statement})
            .then((data) => {
                setSavingEdit(false);
                if (data && data.status === 'OK') {
                    setEditTarget(null);
                    actions.refetch();
                    toast.success(t('client_memory.toast.edited'));
                } else {
                    toast.error(t('client_memory.toast.error'));
                }
            })
            .catch(() => {
                setSavingEdit(false);
                toast.error(t('client_memory.toast.error'));
            });
    };

    /**
     * Compartir (o dejar de compartir) un hecho con el cliente. El store revierte el optimismo solo,
     * así que sin este aviso el usuario ve el toggle volver a su lugar sin ninguna explicación —
     * indistinguible de haberle errado al click. El resto de las acciones del archivo (editar,
     * quitar) ya avisan; ésta era la única muda.
     */
    const handleToggleVisibility = (factId) => {
        actions.toggleVisibility(factId).then((ok) => {
            if (!ok) toast.error(t('client_memory.toast.error'));
        });
    };

    /**
     * El aviso del archivo, con su Deshacer opcional.
     *
     * **Un id POR HECHO**, no uno para todo el archivo. Con un id compartido, quitar A y después B
     * reapuntaba el mismo cartel a B y **A se quedaba sin ningún camino de vuelta**: su «Deshacer»
     * dejaba de existir sin avisar, y el hecho no aparece ni en el mazo ni en «Ruido probable» ni
     * en «Archivado solo» (QA 2026-09-09, hallazgo colateral de QA-mem-13 — hubo que reponerlo
     * desde afuera de la interfaz). Acá los dos hechos están a la vista en la misma lista, así que
     * los dos avisos tienen que poder convivir.
     *
     * `toast.update` y no un `toast()` a secas cuando el id ya está activo: react-toastify 11
     * descarta EN SILENCIO un toast cuyo id ya existe.
     */
    const notify = (factId, message, onUndo) => {
        const toastId = `${TOAST_ID}-${factId}`;
        const content = (
            <div className="iabm-toast" role="status" aria-live="polite">
                <span className="iabm-toast__msg">{message}</span>
                {onUndo && (
                    <button
                        type="button"
                        className="iabm-toast__undo"
                        /**
                         * ⚠️ `stopPropagation` NO es defensivo: el `<ToastContainer>` de la app
                         * corre con `closeOnClick` (`App.js`), así que sin esto el mismo click que
                         * dispara el Deshacer CIERRA el cartel. Los dos PATCH encadenados tardan
                         * ~1,5 s, y para cuando contestan ya no hay a quién actualizar: el aviso de
                         * vuelta no se dibujaba por ninguno de los dos caminos, y lo último que el
                         * usuario había leído decía lo contrario de lo que pasó (QA-mem-12).
                         */
                        onClick={(e) => { e.stopPropagation(); onUndo(); }}
                    >
                        {t('ia_booster.deck.undo')}
                    </button>
                )}
            </div>
        );
        // `autoClose` va en las DOS ramas. `toast.update` sin él hereda el temporizador que ya
        // venía corriendo (C-verbos-03): Quitar X → Deshacer → el cartel se actualiza dos veces sin
        // resetear el reloj → volver a Quitar X dentro de esa ventana nacía con lo que quedara del
        // reloj viejo, que pueden ser 200 ms. Toda la red de una acción sin confirmación cuelga de
        // este número, así que no puede depender de si el cartel ya estaba abierto.
        const options = {autoClose: onUndo ? UNDO_TOAST_MS : undefined};
        if (toast.isActive(toastId)) toast.update(toastId, {render: content, ...options});
        else toast(content, {toastId, ...options});
    };

    /**
     * «Mandar a revisión» un hecho que entró solo: vuelve a la cola para que una persona lo decida.
     *
     * Sin Deshacer, a propósito y por la misma razón que en el mazo: volver a la cola ES el
     * deshacer de "entró solo", y el camino de vuelta ya existe (aprobarlo desde Revisa).
     */
    const handleSendToReview = (fact) => {
        actions.reopen(fact.id).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notify(fact.id, t('ia_booster.auto.toast_reopened'), null);
        });
    };

    /**
     * «Rechazar» un hecho del archivo: sale de la memoria (y del contexto del agente), con vuelta atrás.
     *
     * Es `reject` y no el `DELETE`, para CUALQUIER fila —la que entró sola y la que aprobó una
     * persona—: el rechazo deja fila en la Bitácora con quién lo hizo, se deshace desde este aviso
     * y sigue contando como un «no» de la agencia; el soft-delete no dejaba nada de eso. No pide
     * confirmación: la red es el Deshacer, y por eso su aviso dura más (`UNDO_TOAST_MS`).
     *
     * `removeFromArchive` NO empuja entrada de deshacer en la pila del store —la consume el mazo,
     * que sostiene un paso de sesión por cada entrada— así que la vuelta atrás la ofrece este
     * aviso y la ejecuta `restoreToLibrary`. Ver los dos docstrings en `ClientFactsStore`.
     *
     * ⚠️ **El Deshacer repone el hecho y su marca, pero NO su revisor** (C-verbos-04).
     * `restoreToLibrary` es `reopen` + `approve`, y el backend estampa `reviewed_by` al reabrir: sobre
     * una fila que aprobó un colega, un gesto que se lee como «no pasó nada» re-estampa el revisor con
     * el usuario actual y deja tres filas de Bitácora (reject → reopen → approve). No hay forma de
     * evitarlo desde el Frontend — haría falta que el backend supiera revertir un `reject` preservando
     * la procedencia—, así que si alguna vez molesta, el arreglo va allá.
     *
     * Lo que sí SOBREVIVE es la marca «Entró solo», y conviene saberlo porque el docstring de `reopen`
     * en el store sugiere lo contrario: **verificado en pantalla contra producción el 14/09**, un hecho
     * marcado que se quita y se repone vuelve CON su pastilla y el chip no baja. `reopen()` del backend
     * no toca `_auto_approved` (es marca de ORIGEN, y `entered_automatically()` sólo pide que además
     * esté aprobado); lo que se apaga es el optimismo local del store, que el refetch corrige.
     */
    const handleRemove = (fact) => {
        /**
         * El Deshacer, con REINTENTO si falla.
         *
         * Un hecho quitado queda `REJECTED` + inactivo, y en ese estado **no lo lista ninguna
         * pantalla**: ni el archivo (que es aprobado + vigente), ni el mazo (que lista PENDING), ni
         * «Ruido probable», ni «Archivado solo». El botón de este cartel es la ÚNICA puerta de
         * vuelta que hay en la interfaz. Por eso, cuando la cadena de vuelta falla, el cartel NO
         * puede quedarse sin botón: antes avisaba «no se pudo» con `onUndo` en `null` y ahí se
         * terminaba el camino — había que reponer el hecho desde afuera de la interfaz, que es
         * exactamente lo que pasó el 09/09 con el id de cartel compartido (C-verbos-02).
         *
         * Se volvió más grave con este frente: mientras «Quitar» era sólo de las filas marcadas, lo
         * que se perdía era algo que nadie había curado; ahora alcanza a un hecho aprobado a mano.
         */
        const undo = () => {
            // Dos clicks no disparan dos cadenas sobre el mismo hecho, y el aviso dice
            // «Deshaciendo…» ANTES de mandar nada: son dos PATCH encadenados más el refetch
            // —del orden de cinco segundos— y sin esto el usuario mira una lista que no se
            // mueve con el botón todavía ofreciendo lo que ya apretó. Es el mismo remedio que
            // el mazo tuvo que ponerle a ESTA misma cadena (QA 2026-09-07, QA-cola-06).
            if (undoingRef.current[fact.id]) return;
            undoingRef.current[fact.id] = true;
            notify(fact.id, t('ia_booster.deck.undoing'), null);
            actions.restoreToLibrary(fact.id).then((restored) => {
                undoingRef.current[fact.id] = false;
                // Si NO entró, el cartel se queda con su botón: es un reintento, no un epitafio.
                notify(
                    fact.id,
                    t(restored ? 'ia_booster.archive.toast_restored' : 'ia_booster.archive.undo_failed'),
                    restored ? null : undo,
                );
            });
        };
        // El foco se mueve ANTES de que la fila se desmonte: después, el `<body>` ya se lo llevó.
        if (groupsRef.current) groupsRef.current.focus({preventScroll: true});
        actions.removeFromArchive(fact.id).then((ok) => {
            if (!ok) {
                toast.error(t('ia_booster.deck.error'));
                return;
            }
            notify(fact.id, t('ia_booster.archive.toast_removed'), undo);
        });
    };

    const documentOf = (fact) => (
        (fact.source_document_id && documentsById[fact.source_document_id]) || null
    );

    const renderFact = (fact) => {
        const shared = fact.visibility === VISIBILITY.SHARED;
        const sensitive = isSensitive(fact);
        const busy = isBusy(fact.id);
        const doc = documentOf(fact);
        // El nombre del documento ABRE el documento, en la cita. Sólo cuando hay visor (dentro del
        // Booster) y el material dejó texto que mostrar (`has_raw`): una imagen escaneada no tiene
        // crudo, y un nombre que parece link y no abre nada se lee como un bug.
        const canOpenSource = !!doc && viewer.available && doc.has_raw !== false;
        const auto = isAutoEntered(fact);
        return (
            <div key={fact.id} className={`iabm-fact ${FACT_TYPE_MODIFIER[fact.fact_type] || '-other'}`}>
                <span className="iabm-fact__bar"/>
                <div className="iabm-fact__body">
                    <p className="iabm-fact__statement">{fact.statement}</p>
                    <div className="iabm-fact__meta">
                        <span>{t(originLabelKey(fact.origin))}</span>
                        {/* El título del documento lo escribió la agencia (o el archivo): va como
                            children y en el `aria-label` como texto plano, nunca interpolado en
                            una clave. */}
                        {doc && (canOpenSource ? (
                            <button type="button" className="iabm-fact__source -link"
                                    aria-label={`${t('ia_booster.deck.source_quote_in')}: ${doc.title}`}
                                    onClick={() => viewer.openSource(doc, {quote: fact.source_quote})}>
                                {doc.title}
                                <span className="iabm-fact__source-arrow" aria-hidden="true">&rarr;</span>
                            </button>
                        ) : (
                            <span className="iabm-fact__source">{doc.title}</span>
                        ))}
                        {/* La marca y su ÚNICA salida propia van en la línea de PROCEDENCIA, no
                            en la columna de acciones: «Mandar a revisión» sólo tiene sentido sobre
                            lo que nadie miró, y pegado a la marca se lee por qué esta fila lo
                            ofrece y las otras no. Los verbos que valen para cualquier hecho van a
                            la derecha. El texto del hecho lo escribió la extracción: va en el
                            `aria-label` como texto plano, nunca interpolado en una clave (i18next
                            lo escaparía). */}
                        {/* La pastilla y su verbo van en UN contenedor, no sueltos: el renglón
                            lleva cuatro cosas y, con un título de documento largo, el verbo caía
                            solo en una línea propia, separado de su marca por un salto entero — se
                            leía como un control suelto del hecho, que es lo contrario de por qué
                            está ahí (QA-VIS-verbo-04). Envueltos, el par entero baja junto. */}
                        {auto && (
                            <span className="iabm-fact__autogroup">
                                <span className="iabm-fact__auto">
                                    <Lightning size={10} weight="fill" aria-hidden="true"/>
                                    {t('ia_booster.auto.title')}
                                </span>
                                <button type="button" className="iabm-fact__autoact" disabled={busy}
                                        aria-label={`${t('ia_booster.auto.send_to_review')}: ${fact.statement}`}
                                        onClick={() => handleSendToReview(fact)}>
                                    {t('ia_booster.auto.send_to_review')}
                                </button>
                            </span>
                        )}
                    </div>
                </div>
                <div className="iabm-fact__actions">
                    <button
                        type="button"
                        className={`iabm-share ${shared ? '-on' : ''} ${sensitive ? '-locked' : ''}`}
                        disabled={sensitive || busy}
                        aria-pressed={shared}
                        onClick={() => handleToggleVisibility(fact.id)}
                    >
                        <span className="iabm-share__tg"/>
                        {t('ia_booster.archive.share_client')}
                    </button>
                    {/* Los tipos sensibles (riesgo, comercial) no se comparten NUNCA con el
                        cliente. Se muestra el candado en vez de esconder el control: un toggle
                        que desaparece en algunas filas se lee como un bug, no como una regla. */}
                    {sensitive && (
                        <span className="iabm-fact__lock">{t('ia_booster.archive.share_locked')}</span>
                    )}
                    {/* Dos verbos, del mismo peso y en este orden: primero el que conserva el
                        hecho, después el que lo saca. Ninguno en rojo: «Rechazar» se deshace en un
                        click, y el rojo prometería lo contrario. */}
                    <button type="button" className="iabm-fact__act" disabled={busy}
                            aria-label={`${t('client_memory.action.edit')}: ${fact.statement}`}
                            onClick={() => setEditTarget(fact)}>
                        {t('client_memory.action.edit')}
                    </button>
                    <button type="button" className="iabm-fact__act" disabled={busy}
                            aria-label={`${t('ia_booster.archive.remove')}: ${fact.statement}`}
                            onClick={() => handleRemove(fact)}>
                        {t('ia_booster.archive.remove')}
                    </button>
                </div>
            </div>
        );
    };

    const renderGroup = (key, title, items, renderItem) => {
        const open = isOpen(key);
        // El modificador del TIPO viaja al grupo entero: su título toma el color de la categoría,
        // igual que la barra de cada hecho y el punto de su chip (mockup: `.agh .cat` va coloreado).
        // Sin esto el archivo es una lista de encabezados grises y el color sólo vive en una barra
        // de 3px — el ojo pierde la única señal que le permite recorrer la memoria por categoría.
        const accent = key === NOTES_GROUP ? '-note' : (FACT_TYPE_MODIFIER[key] || '-other');
        const count = items.length;
        // La muestra sólo recorta cuando hay más que el umbral y nadie pidió ver todo: un botón
        // «Ver los 3» debajo de 3 hechos es un botón que no hace nada.
        const previewable = count > GROUP_PREVIEW && !needle;
        const expanded = expandedGroups.has(key);
        const shown = previewable && !expanded ? items.slice(0, GROUP_PREVIEW) : items;
        return (
            <div key={key} className={`iabm-group ${accent} ${open ? '' : '-closed'}`}>
                {/* El caret va a la DERECHA (mockup): a la izquierda competía con la barra de
                    color del tipo, que es lo que identifica al grupo de un vistazo. */}
                <button type="button" className="iabm-group__head" onClick={() => toggleGroup(key)}
                        aria-expanded={open}>
                    <span className="iabm-group__title">{title}</span>
                    <span className="iabm-group__count">{count}</span>
                    <span className="iabm-group__sp"/>
                    {/* UN glifo que GIRA, no dos que se reemplazan (mockup: `.caret` con
                        `transition: transform`). Con el archivo colapsado por defecto, abrir un
                        grupo pasó a ser el gesto principal de la pantalla y saltaba de un frame al
                        otro; el giro lo pone el SCSS sobre `.iabm-group.-closed`. */}
                    <span className="iabm-group__caret" aria-hidden="true">
                        <CaretDown size={12} weight="bold"/>
                    </span>
                </button>
                {open && (
                    <div className="iabm-group__body">
                        {shown.map(renderItem)}
                        {previewable && (
                            <div className="iabm-group__more">
                                <button type="button" className="iabm-btn -link"
                                        aria-expanded={expanded}
                                        onClick={() => toggleExpanded(key)}>
                                    {expanded
                                        ? t('ia_booster.archive.show_less')
                                        : t('ia_booster.archive.show_all', {n: count})}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    /**
     * La cabecera de la pestaña (mockup: `memtab-head`). Le repasa al usuario QUÉ está mirando
     * ahora que la bandeja tiene dos caras, y el punto de color dice de cuál — la misma señal que
     * la tarjeta elegida y el filo de la bandeja, que salen del mismo `--tab`.
     *
     * La trazabilidad al material (mockup: `srclink`) se corrió acá, a la derecha: es la otra cosa
     * que se puede querer hacer desde el encabezado, y suelta arriba dejaba dos renglones de
     * cortesía antes del primer hecho. Sólo dentro del Recorrido, que es donde el panel de Fuentes
     * existe.
     *
     * Se dibuja también cuando el listado FALLÓ: si no, el único estado en el que el usuario
     * necesita orientarse —una caja de error donde esperaba su memoria— es justo el que no dice en
     * qué pestaña está.
     */
    const renderTabHead = () => (
        <div className="iabm-tabhead">
            <span className="iabm-tabhead__flag">
                <span className="iabm-tabhead__dot" aria-hidden="true"/>
                {t('ia_booster.archive.head_flag')}
            </span>
            <span className="iabm-tabhead__src">
                {t('ia_booster.archive.head_src')} <b>{t('ia_booster.archive.head_src_em')}</b>
            </span>
            <span className="iabm-tabhead__sp"/>
            {/* Sin Hechos en el plan no hay material que los haya generado: el link no va. */}
            {drawer.available && !factsLocked && (
                <button type="button" className="iabm-srclink" onClick={() => drawer.openSources()}>
                    <FileText size={12} weight="bold" aria-hidden="true"/>
                    {t('ia_booster.archive.sources_link')}
                    {/* El mockup cierra el link con «→» (`.srclink` … `&rarr;`): es la señal de
                        «esto te lleva a otro lado», y se había perdido al mudarlo a la cabecera. */}
                    <span className="iabm-srclink__arrow" aria-hidden="true">&rarr;</span>
                </button>
            )}
        </div>
    );

    const renderChips = () => (
        <div className="iabm-chips">
            {FACT_TYPE_ORDER.map((type) => {
                const on = activeTypes.has(type);
                const count = typeCounts[type] || 0;
                return (
                    <button
                        key={type}
                        type="button"
                        data-type={type}
                        className={`iabm-chip ${FACT_TYPE_MODIFIER[type]} ${on ? '-on' : ''}`}
                        // Un chip prendido nunca se deshabilita: hay que poder apagarlo aunque su
                        // contador haya quedado en 0 por la búsqueda.
                        disabled={count === 0 && !on}
                        aria-pressed={on}
                        onClick={() => toggleType(type)}
                    >
                        <span className="iabm-chip__dot"/>
                        {t(factTypeLabelKey(type))}
                        <span className="iabm-chip__count">{`(${count})`}</span>
                    </button>
                );
            })}
            {/* El filtro de PROCEDENCIA: es lo que reemplaza a la sección «Entró solo» — verlos
                todos juntos sigue a un click, pero sin sacarlos de la memoria a la que pertenecen.
                A diferencia de los chips de tipo, éste desaparece cuando no hay ninguno: las once
                categorías son una taxonomía fija (un `(0)` ahí informa), y esto es un hecho sobre
                esta memoria — si nada entró solo, el filtro no tiene de qué hablar. */}
            {(autoCount > 0 || autoOnly) && (
                <button
                    type="button"
                    className={`iabm-chip -auto ${autoOnly ? '-on' : ''}`}
                    aria-pressed={autoOnly}
                    onClick={() => setAutoOnly(on => !on)}
                >
                    <Lightning size={10} weight="fill" aria-hidden="true"/>
                    {t('ia_booster.auto.title')}
                    <span className="iabm-chip__count">{`(${autoCount})`}</span>
                </button>
            )}
        </div>
    );

    const renderEmpty = () => {
        const nothingAtAll = library.length === 0 && notes.length === 0;
        return (
            <div className="iabm-empty">
                {nothingAtAll
                    ? <Brain size={34} weight="duotone" className="iabm-empty__icon"/>
                    : <MagnifyingGlass size={30} weight="bold" className="iabm-empty__icon"/>}
                <h4 className="iabm-empty__title">
                    {t(nothingAtAll ? 'ia_booster.archive.empty_title' : 'ia_booster.archive.no_results_title')}
                </h4>
                <p className="iabm-empty__text">
                    {t(nothingAtAll ? 'ia_booster.archive.empty_text' : 'ia_booster.archive.no_results_text')}
                </p>
                <div className="iabm-empty__actions">
                    {!nothingAtAll && (
                        <button type="button" className="iabm-btn -link" onClick={clearFilters}>
                            {t('ia_booster.archive.clear_filters')}
                        </button>
                    )}
                    <Link className="iabm-btn -primary"
                          to={boosterFlowPath(clientId, BOOSTER_STEP_FEED)}>
                        {t('ia_booster.archive.empty_cta')}
                    </Link>
                </div>
            </div>
        );
    };

    // Los Hechos no están en el plan: se dice eso, con la salida a Suscripción. Va ANTES que cargando
    // y que el error porque con la capa apagada el store no pide nada (`hasFactsLayer`), y ninguno de
    // los dos estados es lo que le pasa a esta memoria.
    if (factsLocked) {
        return (
            <div className="iabm-archive">
                {renderTabHead()}
                <div className="iabm-empty -locked">
                    <Lock size={30} weight="bold" className="iabm-empty__icon"/>
                    <h4 className="iabm-empty__title">{t('ia_booster.archive.locked_title')}</h4>
                    <p className="iabm-empty__text">{t('ia_booster.archive.locked_text')}</p>
                    <div className="iabm-empty__actions">
                        <Link className="iabm-btn -primary" to={PLANS_PATH}>
                            {t('ia_booster.archive.locked_cta')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Cargando dibuja la MISMA cabecera que el error, y por el mismo motivo: sin ella la bandeja
    // es un rectángulo vacío —el spinner ni siquiera cae adentro— que no se lee como «cargando»
    // sino como «no hay nada», y al llegar la respuesta todo aparece de golpe y empuja la página
    // (QA 2026-09-09, QA-VIS-mem-06).
    if (loading) {
        return (
            <div className="iabm-archive">
                {renderTabHead()}
                <div className="box-loader"><Spinner color="positive"/></div>
            </div>
        );
    }

    // El listado no se pudo leer: se dice, y se ofrece volver a intentar. Dibujar el archivo vacío
    // acá sería afirmar que esta memoria no tiene nada — con el mazo de Revisa, alimentado por el
    // MISMO store, diciendo justo al lado que no se pudo cargar.
    if (loadFailed) {
        return (
            <div className="iabm-archive">
                {renderTabHead()}
                <div className="iabm-empty -failed">
                    <Warning size={30} weight="bold" className="iabm-empty__icon"/>
                    <h4 className="iabm-empty__title">{t('ia_booster.deck.load_error')}</h4>
                    <div className="iabm-empty__actions">
                        <button type="button" className="iabm-btn -primary" onClick={actions.refetch}>
                            {t('ia_booster.deck.retry')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const nothingToShow = groups.length === 0 && visibleNotes.length === 0;
    /**
     * La memoria está en CERO — no «el filtro no encontró nada».
     *
     * Con la memoria vacía se dibujaban igual los once chips de categoría en «(0)» y el buscador,
     * encima de un cartel que dice que no hay nada: once filtros que no filtran y una búsqueda
     * sobre nada (QA 2026-09-09, QA-VIS-mem-12). El criterio ya existía en esta misma pantalla —el
     * chip «Entró solo» y «Abrir todo» se gatean por su contenido—, sólo faltaba aplicarlo al
     * resto. Ojo: un chip en «(0)» PORQUE la búsqueda no lo alcanza sí se sigue dibujando; eso es
     * un estado del botón y esconderlo se leería como que la categoría no existe.
     */
    const emptyMemory = library.length === 0 && notes.length === 0;

    return (
        <div className="iabm-archive">
            {renderTabHead()}
            {!emptyMemory && <div className="iabm-archive__tools">
                <label className="iabm-search">
                    <MagnifyingGlass size={14} weight="bold"/>
                    <input
                        type="text"
                        className="iabm-search__input"
                        placeholder={t('ia_booster.archive.search_placeholder')}
                        value={searchDraft}
                        onChange={(e) => setSearchDraft(e.target.value)}
                    />
                </label>
                {/* Un INTERRUPTOR, como el mockup: cambia la vista de la memoria por la hoja, y
                    eso es un estado que se prende y se apaga, no una acción. El `aria-pressed` ya
                    lo decía; ahora también se ve. */}
                <button type="button" className={`iabm-iaview ${sheetOpen ? '-on' : ''}`}
                        aria-pressed={sheetOpen}
                        onClick={() => setSheetOpen(open => !open)}>
                    <span className="iabm-iaview__tg" aria-hidden="true"/>
                    {t('ia_booster.archive.ia_view')}
                </button>
            </div>}

            {sheetOpen ? <IaContextSheet clientId={clientId}/> : (
                <>
                    {!emptyMemory && renderChips()}
                    {/* El porqué de la marca, dicho una sola vez y donde se la está mirando. NO
                        dice «nada entra a la memoria sin vos» —el mockup lo afirma en negrita y es
                        falso: con el gate abierto entran hechos solos, que es justo lo que la
                        etiqueta señala—: dice lo que sí es cierto, que sobre todo lo que entra se
                        puede decidir después. */}
                    {autoOnly && (
                        <p className="iabm-archive__hint">{t('ia_booster.auto.hint')}</p>
                    )}
                    {/* «Abrir todo» / «Cerrar todo». Va acá y no al pie: la decisión de cómo mirar
                        el archivo se toma ANTES de scrollearlo. */}
                    {groups.length > 1 && (
                        <div className="iabm-groups-toggle">
                            <button type="button" className="iabm-btn -link"
                                    onClick={() => setAllGroups(!baseOpen)}>
                                {t(baseOpen
                                    ? 'ia_booster.archive.collapse_all'
                                    : 'ia_booster.archive.expand_all')}
                            </button>
                        </div>
                    )}
                    {/* «Posibles duplicados» ya no vive acá: desde el 2026-09-12 es la sección
                        «Duplicados» del acto Revisa (Pablo, 10/09: «es algo para la etapa de
                        revisión»). Memoria es la pantalla de lo curado, no una bandeja más. */}
                    {nothingToShow ? renderEmpty() : (
                        <div className="iabm-groups" ref={groupsRef} tabIndex={-1}>
                            {groups.map(group => renderGroup(
                                group.type,
                                t(factTypeLabelKey(group.type)),
                                group.items,
                                renderFact,
                            ))}
                            {visibleNotes.length > 0 && renderGroup(
                                NOTES_GROUP,
                                t('ia_booster.archive.notes_group'),
                                visibleNotes,
                                note => (
                                    <div key={note.id} className="iabm-fact -note">
                                        <span className="iabm-fact__bar"/>
                                        <div className="iabm-fact__body">
                                            <p className="iabm-fact__statement">{note.text}</p>
                                            <div className="iabm-fact__meta">
                                                <span>{note.author_name || t('ia_booster.archive.note_internal')}</span>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    )}
                </>
            )}

            <FactEditModal
                isOpen={!!editTarget}
                fact={editTarget}
                saving={savingEdit}
                toggle={() => setEditTarget(null)}
                onSave={handleEditSave}
            />
        </div>
    );
}

export default MemoryArchiveView;
